'use strict';

_.templateSettings.interpolate = /{{([\s\S]+?)}}/g;

/**
 * A logging utility library based on SLF4J, for trapping/tracking errors, activity messages, and tracking user activity
 *
 * @license Copyright (c) 2012-2014 The Advisory Board Company
 * @global
 *
 */
var Logger = function Logger(id) {
  window[id] = this;
  this.id = id;
};

/**
 * @lends Logger.prototype
 */
Logger.prototype = new (function LoggerFn() {
  // circular reference for adding methods to Logger
  this.fn = this;

  /**
   * default configuration values applied one each new log.create() method
   *
   * @usage override or add to the configuration of a logger, at any time
   * @see Logger#configuration
   *
   */
  this.defaults = {
    serializer: {
      on: true,
      charactersBetweenToken: '\t',
      withoutToStringFailsafe: ''
    },
    packager: {
      parameterMapping: []
    },
    logChannels: {
      INTERNAL: {
        color: '#000'
      },
      TRACE: {
        color: '#d2d2d4'
      },
      LIBRARY: {
        color: '#000'
      },
      DEBUG: {
        color: '#39E24D'
      },
      DEPRECATED: {
        color: '#000'
      },
      INFO: {
        color: '#0F4FA8'
      },
      ISSUE: {
        color: '#000'
      },
      PROBLEM: {
        color: '#000'
      },
      EVENT: {
        color: '#000'
      },
      SUCCESS: {
        color: '#4FA80F'
      },
      WARN: {
        color: '#FF6A00'
      },
      ERROR: {
        color: '#FF2300'
      },
      FATAL: {
        color: '#DB0058'
      },
      UNCAUGHT: {
        color: '#231212'
      }
    },
    defaultChannel: {
      priority: undefined,
      on: true
    },
    defaultSync: {
      activeInitially: false,
      serialized: true,
      queued: false,
      disallow: [],
      allow: []
    },
    defaultQueuedSync: {
      accumulationLimit: 100,
      queue: []
    },
    codes: {},
    syncs: {
      console: {
        template: _.template('{{ logger }} ({{ channel }}) {{ time }} {{ message }} {{ serialized }}'),
        activeInitially: true
      },
      memory: {
        queue: [],
        accumulationLimit: 100,
        circularIndex: 0,
        serialized: false,
        queued: false
      }
    },
    syncsFn: {
      /**
       * sync for console
       *
       * @param entry object holding message, channel, and logger-id
       * @param channelDef definition of channel (info, warn, error, ...)
       */
      console: function loggerSyncConsole(entry, channelDef) {
        var consoleMethods = ['trace', 'debug', 'info', 'warn', 'error'],
          fn = _.contains(consoleMethods, channelDef.id) ? channelDef.id : 'log';

        if (window.console && window.console[fn]) {
          window.console[fn]('%c' + entry.templatized, 'color:' + channelDef.color);
        }
      },

      /**
       * sync to an array
       *
       * @default list in array configuration
       * @param entry object holding message, channel, and logger-id
       */
      memory: function loggerSyncMemory(entry) {
        var config = this._.configuration.syncs.memory;

        config.queue[config.circularIndex] = entry;
        config.circularIndex = (config.circularIndex + 1) % config.accumulationLimit;
      }
    },
    syncsQueuedFn: {}
  };

  /**
   *
   * @param additionalFunctions
   * @returns {Logger}
   */
  this.addFunctionality = function addFunctionality_logger(additionalFunctions) {
    this.fn = _.merge(this.fn, additionalFunctions);
    return this;
  };

  /**
   * create a new logger, for instance for a different purpose or section of the application
   *
   * @memberof Logger
   * @param id name of log object window[id]
   * @returns {Logger} new log object
   */
  this.create = function loggerCreate(id) {
    return new Logger(id).reset();
  };

  /**
   * resets all data for the logger, but persists the configuration object
   *
   * @memberof Logger
   * @see Logger#configure
   * @returns {Logger}
   */
  this.reset = function loggerReset() {
    this._ = {};
    this._.on = true;
    this._.configuration = {};
    this._.listener = [];
    this._.listeners = {};
    this._.syncs = {};
    this._.entry = undefined;
    this._.forceSync = false;
    this._.getChannelName = getChannelName;
    this._.getChannel = getChannel;

    // use translation codes if you want to build a translation library so your
    // error messages are shorter and are translated when added to message objects
    this._.eventCodes = {};
    this._.eventCodeHierarchy = {};

    this.configure(this._.configuration);
    return this;
  };

  /**
   * @memberof Logger
   * @return last entry logged
   */
  this.entry = function loggerEntry() {
    return this._.entry;
  };

  /**
   * force queue to empty and write to any queued syncs
   * @returns {Logger}
   */
  this.flush = function loggerFlush() {
    this._.forceSync = true;

    _.each(
      this._.configuration.syncsQueuedFn,
      function (queuedSyncFn) {
        queuedSyncFn.call(this);
      },
      this
    );

    this._.forceSync = false;
    return this;
  };

  /**
   * configure logger, but overlay the current configuration, instead of erase it like Logger#configure
   * @deprecated Logger#configurePreserve
   * @param overlayedConfiguration
   * @returns {Logger}
   */
  this.configurePreserve = function configurePreserve_logger(overlayedConfiguration) {
    return this.configure(_.merge(this.configuration(), overlayedConfiguration));
  };

  /**
   * configure logger, but overlay the current configuration, instead of erase it like Logger#configure
   * @param updateConfigurationWith
   * @returns {Logger}
   */
  this.update = function update_logger(updateConfigurationWith) {
    return this.configure(_.merge(this.configuration(), updateConfigurationWith));
  };

  /**
   * @memberof Logger
   * @returns current configuration object for logger
   */
  this.configuration = function loggerConfiguration() {
    return this._.configuration;
  };

  /**
   * used for debugging, if the condition is true, nothing happens; if the condition is false, a fatal error
   * message is sent, and the application fails
   *
   * @memberof Logger
   * @param condition boolean expression
   * @param message passed off to <logger>.fatal
   * @returns {Logger}
   */
  this.assert = function loggerAssert(condition, message) {
    if (!condition) {
      this.fatal(message);
      throw message;
    }

    return this;
  };

  /**
   * assigns the global#onerror global error handler, there can only be one
   * the method signature of the global error handler is (msg, url, linenumber)
   *
   * @param arg {String|Function} if a string, then it determines the log channel
   * used to send messages (default:uncaught); if a function, then must return (true/false) to set if the
   * browser error handling is silent or active and stops execution (true is silent mode)
   *
   * @note if a method exists, then a warning is given, but the assign will take place
   * @memberof Logger
   * @returns {Logger}
   */
  this.global = function loggerGlobal(arg) {
    var logger = this,
      channel,
      fn;

    channel = !!arg && _.isString(arg) ? arg : 'uncaught';

    fn = _.isFunction(arg)
      ? arg
      : function loggerOnError(msg, url, linenumber) {
          logger[channel](msg, {
            url: url,
            line: linenumber
          });
          return true;
        };

    if (window.onerror) {
      throw new EABError('utility-js-log', 100);
    }

    window.onerror = fn;
    return this;
  };

  /**
   * reconfigure the logger, if a logChannelEnum is removed or added, then the corresponding method will be removed or added
   *
   * @memberof Logger
   * @param configuration override default configuration, at any time, all methods draw from the configuration at runtime
   * @returns {Logger}
   */
  this.configure = function loggerConfigure(configuration) {
    // remove all log channel functions
    var existingLogFunctions = this.channels ? this.channels : [];

    _.each(
      existingLogFunctions,
      function (channelDef) {
        delete this[channelDef.id];
      },
      this
    );

    // update configuration
    configuration = this._.configuration = _.merge({}, this.defaults, configuration);
    this.channels = configuration.logChannels;

    assignDefaultLogChannelValues.call(this, this.channels);
    assignDefaultSyncConfigurations.call(this, configuration.syncs);
    assignQueuedSyncReconfigurations.call(this, configuration);
    assignReverseLookupCodeHierarchy.call(this, configuration.codes, []);

    // assign syncs initially
    this._.syncs = {};
    _.each(
      configuration.syncs,
      function (sync, syncName) {
        if (sync.activeInitially) {
          this.addSync(syncName);
        }
      },
      this
    );

    _.each(
      this.channels,
      function (channelDef) {
        this._.listeners[channelDef.id] = [];
      },
      this
    );

    // add log channel functions
    return createLogFn.call(this);
  };

  /**
   * assign default values for channels, useful for limiting code
   */
  var assignDefaultLogChannelValues = function assignDefaultLogChannelValues(logChannels) {
    var i = 0,
      defaultChannel = this._.configuration.defaultChannel;

    _.each(logChannels, function (channel, key) {
      channel = _.extend(channel, defaultChannel);
      channel.id = key.toLowerCase();
      channel.priority = channel.priority === undefined ? i : channel.priority;
      i++;
    });
  };

  /**
   * assign default values to sync configurations
   */
  var assignDefaultSyncConfigurations = function assignDefaultSyncConfigurations(syncConfigurations) {
    _.each(
      syncConfigurations,
      function (sync) {
        _.defaults(sync, this.defaultSync);
      },
      this._.configuration
    );

    syncConfigurations.console.activeInitially = true;
  };

  /**
   * re-write any sync functions with the queued override if queued flag is set
   */
  var assignQueuedSyncReconfigurations = function assignQueuedSyncReconfigurations(configuration) {
    _.each(
      configuration.syncs,
      function (syncConfig, syncName) {
        if (syncConfig.queued) {
          this.syncs[syncName] = _.defaults(syncConfig, this.defaultQueuedSync);
          this.syncsQueuedFn[syncName] = _.partial(
            overrideSyncFunctionWithQueuedSync,
            this.syncsFn[syncName],
            syncConfig,
            syncName
          );
        }
      },
      configuration
    );
  };

  /**
   * create a reverse lookup for each code that delivers all of the hierarchy used to describe the code
   *
   * @param codes codes off of configuration
   * @param stack track progress to map heirarchy
   */
  var assignReverseLookupCodeHierarchy = function assignReverseLookupCodeHierarchy(codes, stack) {
    _.each(
      codes,
      function (code, codeKey) {
        if (_.isString(code)) {
          this._.eventCodes[codeKey] = code;
          this._.eventCodeHierarchy[codeKey] = _.isEmpty(stack) ? stack : _.clone(stack);
        } else {
          stack.push(codeKey);
          assignReverseLookupCodeHierarchy.call(this, code, stack);
          stack.pop();
        }
      },
      this
    );
  };

  /**
   * wrap the sync function and call after the accumulation limit for the queue is reached
   *
   * @param configuration partially apply configuration for sync
   * @param syncName partially apply name of sync
   * @param entry standard syncFn parameter, the entry to sync
   * @param channelDef not used, but will be passed in
   *
   * TODO evaluate if we can assign length to zero or if we should assign new queue for memory sanity, qualification is based on interoperability with worker or async.js library
   */
  var overrideSyncFunctionWithQueuedSync = function overrideSyncFunctionWithQueuedSync(
    originalSyncFn,
    syncConfiguration,
    syncName,
    entry
  ) {
    if (!!this._.forceSync || syncConfiguration.queue.length >= syncConfiguration.accumulationLimit - 1) {
      originalSyncFn.call(this, syncConfiguration.queue);
      syncConfiguration.queue = [];
    } else {
      syncConfiguration.queue.push(entry);
    }
  };

  /**
   * add logChannelEnum methods to the logger
   *
   * @memberof Logger
   * @returns {Logger}
   */
  var createLogFn = function loggerCreateLogFn() {
    _.each(
      this.channels,
      function (channelDef) {
        var channel = channelDef.id.toLowerCase();

        this[channel.toUpperCase()] = channel;
        this[channel] = function logWithPayload(message) {
          return this._.on && channelDef.on
            ? logFn.call(this, channelDef, message, this.package.apply(this, Array.prototype.splice.call(arguments, 1)))
            : this;
        };
      },
      this
    );

    return this;
  };

  /**
   * the workhorse of the application, each log function programs this function
   *
   * @important do not keep a reference to the description object past the life of the event handler or sync
   * @param channelDef definition of channel (info, warn, error, ...)
   * @param entry object holding message, channel, and logger-id
   * @returns {Logger}
   */
  var logFn = (function hideCommonLogMessage() {
    var orderedEntry = 0;

    return function loggerLogFn(channelDef, message, payload) {
      var entry = {};
      var codedMessage = message;
      var translationExists = this._.eventCodes[message] !== undefined;
      var translatedMessage = translationExists
        ? _.isFunction(this._.eventCodes[message])
          ? this._.eventCodes[message](channelDef, message)
          : this._.eventCodes[message]
        : message;

      if (translationExists) {
        entry.code = codedMessage;
        entry.category = this._.eventCodeHierarchy[entry.code];
      }

      entry.order = orderedEntry;
      entry.time = _.now();
      entry.logger = this.id;
      entry.channel = channelDef.id;
      entry.message = translatedMessage;
      entry.payload = payload;

      orderedEntry += 1;

      this._.entry = entry;
      logEntryDispatch.call(this, entry);
      return this;
    };
  })();

  /**
   * send entry to all listeners and dispense to syncs
   */
  var logEntryDispatch = function loggerLogEntryDispatch(entry) {
    dispatchToEventListeners.call(this, entry);
    dispatchToSyncs.call(this, entry);
    return this;
  };

  /**
   * write entry to all event listeners
   */
  var dispatchToEventListeners = function dispatchToEventListeners(entry) {
    _.each(
      this._.listener,
      function (fn) {
        fn.call(this, entry, entry);
      },
      this
    );

    _.each(
      this._.listeners[entry.channel],
      function (fn) {
        fn.call(this, entry, entry);
      },
      this
    );
  };

  /**
   * write entry to to all syncs
   */
  var dispatchToSyncs = function dispatchToSyncs(entry) {
    _.each(
      this._.syncs,
      function (sync, syncName) {
        dispatchOneEntry.call(this, sync, syncName, entry);
      },
      this
    );
  };

  /**
   * if should write entry to sync, then serialize data and send to sync
   */
  var dispatchOneEntry = function loggerDispatchOneEntry(sync, syncName, entry) {
    var syncConfig = this._.configuration.syncs[syncName];

    // performance bottleneck - could be asked 100 times; but we need to evaluate for each entry because channel may be blocked
    if (shouldWrite.call(this, syncName, entry.channel)) {
      if (!!syncConfig.serialized && !entry.serialized) {
        entry.serialized = serializePayload.call(this, syncName, entry);
      }

      if (!!syncConfig.serialized && !!syncConfig.template && !entry.templatized) {
        entry.templatized = syncConfig.template(entry);
      }

      sync.call(this, entry, getChannel.call(this, entry.channel));
    }
  };

  /**
   * @param entry entry holds a payload
   * @returns {String} the payload serialized, or empty if there is no payload
   */
  var serializePayload = function loggerSerializePayload(syncName, entry) {
    var config = this._.configuration;

    return config.serializer.on && config.syncs[syncName].serialized && entry.payload !== undefined
      ? this.serialize(entry.payload)
      : '';
  };

  /**
   * is channel in the sync's disallow list, if so then we should not write to that sync
   * is channel in the sync's allow list, if so then we should write to that sync
   * allow supercedes disallow
   *
   * @returns {boolean}
   */
  var shouldWrite = function loggerShouldWrite(syncName, channelName) {
    var syncConfig = this._.configuration.syncs[syncName],
      hasAllowRules = syncConfig.allow.length > 0,
      hasDisallowRules = syncConfig.disallow.length > 0;

    if (!hasAllowRules && !hasDisallowRules) {
      return true;
    } else if (hasAllowRules && _.contains(syncConfig.allow, channelName)) {
      return true;
    } else if (hasAllowRules) {
      return false;
    } else if (hasDisallowRules && _.contains(syncConfig.disallow, channelName)) {
      return false;
    } else if (hasDisallowRules) {
      return true;
    } else {
      // NOTE never executes; true/true never happens because t/f or f/t catches with a return and f/f is above
      return false;
    }
  };

  /**
   * erase logger, but the core object is returned so you can use it to create a new object
   *
   * @example <caption>build a new logger from the old</caption>
   * new log.destroy()('logger');
   * logger.info('use logger now instead of log');
   *
   * @memberof Logger
   * @returns {Logger} core logger function to create a new logger
   */
  this.destroy = function loggerDestroy() {
    window[this.id] = undefined;
    delete window[this.id];
    return Logger;
  };

  /**
   * serialize an object for string transport
   *
   * @memberof Logger
   * @param token convert one object to a string
   * @returns {Mixed|*}
   */
  this.serialize = function loggerSerializer(token) {
    var rtn;
    var config = this._.configuration.serializer;

    try {
      if (canBeStringified(token)) {
        rtn = token.toString();
      } else if (JSON) {
        rtn = JSON.stringify(token, null, config.charactersBetweenToken);
      } else if (_.isFunction(config.withoutToStringFailsafe)) {
        rtn = config.withoutToStringFailsafe(token);
      } else {
        rtn = config.withoutToStringFailsafe;
      }
    } catch (e) {
      throw new EABError('utility-js-log', 200, {
        error: e,
        token: token
      });
    } finally {
      return rtn;
    }
  };

  /**
   * will toString work on an object
   *
   * @param o the string to inspect
   * @return {Boolean} true if o.toString() will work
   */
  var canBeStringified = function canBeStringified(o) {
    return (
      o !== undefined &&
      o !== null &&
      !_.isPlainObject(o) &&
      !_.isArray(o) &&
      o.toString &&
      o.toString() !== '[object Object]'
    );
  };

  /**
   * collect all parameters given to a log function and coalesce them into one object, referred to as a payload
   *
   * @memberof Logger
   * @returns {PlainObject}
   */
  this.package = function loggerPackager() {
    var packaged, mapping;
    var args = Array.prototype.slice.apply(arguments);

    if (args.length === 0) {
      return undefined;
    } else if (args.length === 1) {
      return args[0];
    }

    // TODO split this off into a different function

    packaged = {};
    mapping = this._.configuration.packager.parameterMapping;

    _.each(
      args,
      function (o, i) {
        var key = mapping.length > i ? mapping[i] : 'p' + i;

        packaged[key] = o;
      },
      this
    );

    return packaged;
  };

  /**
   * filters messages based on string or regular expressions, for
   * example if messages are coded C0XXX and C1XXX and you want to
   * filter with a regular expression only C1*** messages
   *
   * @memberof Logger
   * @param ifExistsInMessageThenAllow if this pattern or string exists in a message, then allow the function call
   * @param fn the callback function to be called if the first parameter matches
   */
  this.only = function loggerOnly(ifExistsInMessageThenAllow, fn) {
    return function filteredLogHandler(entry) {
      var doesNotExistInMessage;
      var NO_MATCH = -1;

      if (ifExistsInMessageThenAllow) {
        doesNotExistInMessage = entry.message && entry.message.search(ifExistsInMessageThenAllow) === NO_MATCH;
        doesNotExistInMessage =
          doesNotExistInMessage && entry.code && entry.code.search(ifExistsInMessageThenAllow) === NO_MATCH;
        if (doesNotExistInMessage) {
          return false;
        }
      }

      fn(entry);
      return true;
    };
  };

  /**
   * sets the lowest channel to include in logging
   * (trace, debug, info, warn, error, fatal)
   *
   * @memberof Logger
   * @param channel enum or id string ('warn', ...)
   * @returns {Logger}
   */
  this.lowest = function loggerLowest(channel, sync) {
    var lowestChannelOrdinal = getChannel.call(this, channel).priority;

    _.each(
      _.filter(
        this.channels,
        function filterOutChannels(channelDef) {
          return channelDef.priority < lowestChannelOrdinal;
        },
        this
      ),
      function turnChannelsOff(channelDef) {
        this.disable(channelDef.id, sync);
      },
      this
    );

    return this;
  };

  /**
   * sets the highest channel to include in logging
   * (trace, debug, info, warn, error, fatal)
   *
   * @memberof Logger
   * @param channel enum or id string ('warn', ...)
   * @returns {Logger}
   */
  this.highest = function loggerHighest(channel, sync) {
    var highestChannelOrdinal = getChannel.call(this, channel).priority;

    _.each(
      _.filter(
        this.channels,
        function filterOutChannels(channelDef) {
          return channelDef.priority > highestChannelOrdinal;
        },
        this
      ),
      function turnChannelsOff(channelDef) {
        this.disable(channelDef.id, sync);
      },
      this
    );

    return this;
  };

  /**
   * query status of a logger, a channel, or a channel against a sync
   *
   * @memberof Logger
   * @param channel enum or id string ('warn', ...)
   * @param sync (optional) enable this channel for just one sync, it will not enable the channel
   * @returns {boolean} true if channel is enabled
   */
  this.enabled = function loggerEnabled(channel, sync) {
    if (arguments.length === 0) {
      return this._.on === true;
    } else if (sync === undefined) {
      return getChannel.call(this, channel).on === true;
    } else {
      return querySyncChannelPermissions.call(this, channel, sync, 'allow');
    }
  };

  /**
   * set this channel, so if we call log.<channel>('message') then it will output to syncs
   *
   * @memberof Logger
   * @param channel enum or id string ('warn', ...)
   * @param sync (optional) enable this channel for just one sync, it will not enable the channel
   * @returns {Logger}
   */
  this.enable = function loggerEnable(channel, sync) {
    if (arguments.length === 0) {
      this._.on = true;
    } else if (sync === undefined) {
      getChannel.call(this, channel).on = true;
    } else {
      syncEnableForChannel.call(this, channel, sync);
    }

    return this;
  };

  /**
   * enable that a log channel should be allowed to access a sync;
   * the sync must be active and added and the log channel must also be active
   */
  var syncEnableForChannel = function loggerSyncEnableForChannel(channel, sync) {
    return alterSyncChannelPermissions.call(this, channel, sync, 'allow', 'disallow');
  };

  /**
   * query status of a logger, a channel, or a channel against a sync
   *
   * @memberof Logger
   * @param channel enum or id string ('warn', ...)
   * @param sync (optional) enable this channel for just one sync, it will not enable the channel
   * @returns {boolean} true if channel is disabled
   */
  this.disabled = function disabled_logger(channel, sync) {
    if (arguments.length === 0) {
      return this._.on === false;
    } else if (sync === undefined) {
      return getChannel.call(this, channel).on === false;
    } else {
      return querySyncChannelPermissions.call(this, channel, sync, 'disallow');
    }
  };

  /**
   * set this channel, so if we call log.<channel>('message') then it will NOT output to syncs
   *
   * @memberof Logger
   * @param channel enum or id string ('warn', ...)
   * @param sync (optional) disable this channel for just one sync, it will not disable the channel
   * @returns {Logger}
   */
  this.disable = function loggerDisable(channel, sync) {
    if (arguments.length === 0) {
      this._.on = false;
    } else if (sync === undefined) {
      getChannel.call(this, channel).on = false;
    } else {
      syncDisableForChannel.call(this, channel, sync);
    }

    return this;
  };

  /**
   * disable that a log channel should be allowed to access a sync;
   * even if the sync must be active and added and the log channel must also be active
   */
  var syncDisableForChannel = function loggerSyncDisableForChannel(channel, sync) {
    return alterSyncChannelPermissions.call(this, channel, sync, 'disallow', 'allow');
  };

  /**
   * supports matching sync-channel permissions
   *
   * @see Logger#enabled
   * @see Logger#disabled
   * @param channel
   * @param sync
   * @returns {boolean}
   */
  var querySyncChannelPermissions = function querySyncChannelIsAllowed_logger(channel, sync, permission) {
    var syncConfiguration = this._.configuration.syncs[sync];
    var isAllowed = permission === 'allow';

    return (
      isAllowed === _.contains(syncConfiguration.allow, channel) &&
      isAllowed !== _.contains(syncConfiguration.disallow, channel)
    );
  };

  /**
   * called by syncDisableForChannel or syncEnableForChannel to manage the allow and disallow lists
   * @param addTo either disallow or allow array
   * @param removeFrom opposite of addTo
   */
  var alterSyncChannelPermissions = function alterSyncChannelPermissions(channel, sync, addTo, removeFrom) {
    var index;
    var syncConfiguration = this._.configuration.syncs[sync];

    // add to disallow
    if (syncConfiguration[addTo].indexOf(channel) === -1) {
      syncConfiguration[addTo].push(channel);
    }

    // erase from allow
    index = syncConfiguration[removeFrom].indexOf(channel);
    syncConfiguration[removeFrom].splice(index, 1);

    return this;
  };

  /**
   * register a method to be called for log messages of a certain channel or all log messages if no channel if provided
   *
   * @memberof Logger
   * @param channel enum or id string ('warn', ...)
   * @param fn function to call when this logger is called
   * @returns {Logger}
   */
  this.on = function loggerOn(channel, fn) {
    if (fn === undefined) {
      return onAnyChannel.call(this, channel);
    }

    _.each(
      channel.split(' '),
      function (ch) {
        onChannel.call(this, ch, fn);
      },
      this
    );

    return this;
  };

  /**
   * add listener to specific channel
   */
  var onAnyChannel = function onAnyChannel(fn) {
    this._.listener.push(fn);
    return this;
  };

  /**
   * add listener to any channel
   */
  var onChannel = function onChannel(channel, fn) {
    var channelName = getChannelName(channel);

    if (!_.isArray(this._.listeners[channelName])) {
      throw new EABError('utility-js-log', 300, {
        channelName: channelName
      });
    }

    this._.listeners[channelName].push(fn);
    return this;
  };

  /**
   * unregister all listeners or just one listener if provided
   *
   * @memberof Logger
   * @param channel enum or id string ('warn', ...)
   * @param fn function to call when this logger is called
   * @returns {Logger}
   */
  this.off = function loggerOff(channel, fn) {
    if (fn === undefined) {
      return offAnyChannel.call(this, channel);
    }

    _.each(
      channel.split(' '),
      function (ch) {
        offChannel.call(this, ch, fn);
      },
      this
    );

    return this;
  };

  /**
   * remove listener from specific channel
   */
  var offAnyChannel = function offAnyChannel(fn) {
    if (fn !== undefined) {
      _.remove(this._.listener, fn);
    } else {
      this._.listener = [];
    }

    return this;
  };

  /**
   * remove listener to any channel
   */
  var offChannel = function offChannel(channel, fn) {
    _.remove(this._.listeners[getChannelName(channel)], fn);
  };

  /**
   * assign a sync that will intercept all active messages, intended to write the messages to another source
   *
   * @memberof Logger
   * @default console
   * @param sync string (ie. 'session') or a function with the signature (<channelDef>, <aboutMessage>)
   * @returns {Logger}
   */
  this.addSync = function loggerSyncAdd(sync, customFn) {
    var syncConfig = (this._.configuration.syncs[sync] =
      this._.configuration.syncs[sync] || _.clone(this._.configuration.defaultSync));

    if (_.isFunction(customFn)) {
      this._.syncs[sync] = customFn;
    } else if (syncConfig.queued) {
      this._.syncs[sync] = this._.configuration.syncsQueuedFn[sync];
    } else {
      this._.syncs[sync] = this._.configuration.syncsFn[sync];
    }

    return this;
  };

  /**
   * remove a sync previously added
   *
   * @memberof Logger
   * @returns {Logger}
   */
  this.removeSync = function loggerSyncRemove(sync) {
    delete this._.syncs[sync];
    return this;
  };

  /**
   *
   * @memberof Logger
   * @returns current configuration object for sync
   */
  this.configurationOfSync = function loggerConfigurationOfSync(syncName) {
    return this._.configuration.syncs[syncName];
  };

  /**
   * apply event codes to configuration or retrieve codes
   *
   * @param codeObject if not undefined, then apply codes to configuration
   * @returns {Logger}
   */
  this.codes = function codes_logger(codeObject) {
    if (!!codeObject) {
      return this.update({
        codes: codeObject
      });
    }

    return this._.eventCodes;
  };

  /**
   * return the channel name of a channel enum
   *
   * @param channel enum or id string ('warn', ...)
   * @returns {string}
   */
  var getChannelName = function GetChannelName_logger(channel) {
    return channel.toLowerCase();
  };

  /**
   * return the channel Enumeration of a channel enum
   *
   * @param channel enum or id string ('warn', ...)
   * @returns {string}
   */
  var getChannel = function GetChannel_logger(channel) {
    return this._.configuration.logChannels[channel.toUpperCase()];
  };
})();

var log = new Logger('log').reset();

if (typeof module === 'object' && module.exports) {
  module.exports = log;
} else {
  window.log = log;
}



// WEBPACK FOOTER //
// ./app/vendor/utility-js-log/src/main.js